import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE from "../config/api";
import BottomTabBar from "../components/BottomTabBar";
import StoreSwitcher from "../components/StoreSwitcher";
import { auth } from "../config/config-firebase";
import useMerchantStores, {
  isDefaultStoreRow,
  ACTIVE_CUSTOMER_ID_CHANGED_EVENT,
} from "../hooks/useMerchantStores";
import { formatScheduleDaysLabel } from "../utils/promoScheduleApi";
import {
  isAdminMerchantMode,
  getActiveMerchantCustomerId,
  isPromoLoginMode,
} from "../utils/adminMerchantMode";
import {
  Calendar,
  Plus,
  Trash2,
  Pencil,
  X,
  Clock,
  Sun,
  Eye,
  Image as ImageIcon,
  PlayCircle,
} from "lucide-react";

export default function ScheduleHubPage() {
  const navigate = useNavigate();

  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // eye button state
  const [viewingSchedule, setViewingSchedule] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [showImagePreviewModal, setShowImagePreviewModal] = useState(false);
  const [uid, setUid] = useState(null);

  // Schedules are scoped by the active store's own customer_id (see
  // getMerchantStoreDetails — each store row carries its own customer_id).
  // Resolve uid the same way StoreListPage/StoreSwitcher do, then read the
  // default store's customer_id off the shared hook instead of trusting a
  // possibly-stale localStorage "customer_id".
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUid(currentUser?.uid || localStorage.getItem("customer_id") || null);
    });
    return () => unsubscribe();
  }, []);

  // Admin Merchant Mode: use the merchant customer_id the admin selected in
  // LoginAsMerchant directly, bypassing the Firebase-uid-based store hook
  // below (which never applies to an admin session).
  //
  // Promo Login Mode: same situation — a promo device links via
  // customerId + phone (PromoLogin.js) and never signs into Firebase, so
  // there's no real uid for the hook to resolve either (see Mode.js).
  const adminMerchantMode = isAdminMerchantMode();
  const promoLoginMode = isPromoLoginMode();
  const bypassStoreHook = adminMerchantMode || promoLoginMode;
  const { stores, loadingStores, fetchStores } = useMerchantStores(bypassStoreHook ? null : uid);
  const activeStore = stores.find(isDefaultStoreRow) || stores[0] || null;

  // Promo Login Mode has no `stores`/`activeStore` of its own (the hook is
  // bypassed above), so its customerId has to come from localStorage
  // instead — but reading it inline on every render isn't reactive: it
  // won't pick up a switch made via StoreSwitcher below (a sibling
  // component, same page) until something re-renders this component. Track
  // it as state instead, seeded from localStorage and kept in sync by the
  // ACTIVE_CUSTOMER_ID_CHANGED_EVENT listener underneath.
  const [promoCustomerId, setPromoCustomerId] = useState(() => localStorage.getItem("customer_id"));
  const customerId = adminMerchantMode
    ? getActiveMerchantCustomerId()
    : promoLoginMode
    ? promoCustomerId
    : (activeStore?.customer_id || null);
  const storesLoading = bypassStoreHook ? false : loadingStores;

  // StoreSwitcher (rendered below) uses its OWN useMerchantStores instance,
  // so switching the default store there doesn't touch this page's copy of
  // `stores` — without this, the newly-active store's schedules only show up
  // after a full page reload. Refetch this instance's stores whenever a
  // switch happens anywhere on the page. In Promo Login Mode `fetchStores`
  // is a no-op (the hook was never given a uid to work with), so update
  // `promoCustomerId` directly instead — that's what actually drives
  // `customerId` for this mode.
  useEffect(() => {
    if (adminMerchantMode) return;
    const handleActiveStoreChanged = (event) => {
      if (promoLoginMode) {
        setPromoCustomerId(event?.detail?.customerId || localStorage.getItem("customer_id"));
        return;
      }
      fetchStores();
    };
    window.addEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, handleActiveStoreChanged);
    return () => window.removeEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, handleActiveStoreChanged);
  }, [adminMerchantMode, promoLoginMode, fetchStores]);

  // ─── 1. Fetch schedule list ───────────────────────────────────────────────
  const fetchSchedules = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/getSchedules`, {
        params: { customerId: id },
      });
      setSchedules(data?.data || []);
    } catch (err) {
      console.error("Failed to load schedules", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("customer_id")) {
      navigate("/");
    }
  }, [navigate]);

  // Fetches once the active store resolves, and re-fetches in place
  // whenever it changes (e.g. via the StoreSwitcher above).
  useEffect(() => {
    if (storesLoading || !customerId) return;
    fetchSchedules(customerId);
  }, [customerId, storesLoading]);

  // ─── 2. Eye button → fetch single schedule, then resolve its image ids ────
  // /getSchedule returns digitalAdsId as raw ObjectId strings (not populated
  // documents), so we look them up against /findAll to get S3link/title/etc.
  const handleViewImages = async (schedule) => {
    setShowImagePreviewModal(true);
    setViewingSchedule(null);
    setViewLoading(true);
    try {
      const [{ data: scheduleRes }, { data: adsRes }] = await Promise.all([
        axios.get(`${API_BASE}/getSchedule`, {
          params: { scheduleId: schedule._id },
        }),
        axios.get(`${API_BASE}/findAll`),
      ]);

      const sched = scheduleRes?.data;
      if (!sched) {
        setViewingSchedule(null);
        return;
      }

      const adsById = new Map(
        (adsRes?.data || []).map((ad) => [String(ad._id), ad]),
      );
      const populatedImages = (sched.digitalAdsId || [])
        .map((id) => adsById.get(String(id)))
        .filter(Boolean);

      setViewingSchedule({ ...sched, digitalAdsId: populatedImages });
    } catch (err) {
      console.error("Failed to load schedule images", err);
      setShowImagePreviewModal(false);
    } finally {
      setViewLoading(false);
    }
  };

  const closePreview = () => {
    setShowImagePreviewModal(false);
    setViewingSchedule(null);
  };

  // ─── 3. Delete ────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await axios.post(`${API_BASE}/deletePromoSchedule`, {
        scheduleId: deleteConfirm,
      });
      await fetchSchedules(customerId); // re-fetch from backend after delete
    } catch (err) {
      console.error("Failed to delete schedule", err);
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  // ─── 4. Helpers to read from MongoDB shape ────────────────────────────────
  // MongoDB shape:
  //   s._id, s.scheduleName
  //   s.schedule[0].allTime, s.schedule[0].timeWindow[0].start/end
  //   s.schedule[0].allDays, s.schedule[0].days, s.schedule[0].timezone
  //   s.digitalAdsId → populated array of { _id, title, S3link, file_type }

  const timeLabel = (s) => {
    const sched = s.schedule?.[0] || {};
    if (sched.allTime) return "All day";
    const start = sched.timeWindow?.[0]?.start;
    const end = sched.timeWindow?.[0]?.end;
    if (start && end) return `${start} – ${end}`;
    return "All day";
  };

  const daysLabel = (s) => {
    const sched = s.schedule?.[0] || {};
    if (sched.allDays) return "All days";
    return formatScheduleDaysLabel(sched.days || []);
  };

  const timezoneLabel = (s) => {
    const tz = s.schedule?.[0]?.timezone || "";
    return tz;
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col w-full min-w-0">

      <StoreSwitcher />

      {/* ── List ── */}
      <div className="px-3 sm:px-4 pt-5 sm:pt-6 flex-1 pb-28 max-w-2xl w-full mx-auto min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900">
              Schedule Manager
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Manage and organise your display schedules.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/create-schedule")}
            className="flex items-center gap-1.5 bg-blue-600 text-white text-xs sm:text-sm font-bold px-3 sm:px-4 py-2 rounded-full flex-shrink-0"
          >
            <Plus size={14} /> Create Schedule
          </button>
        </div>

        <div className="flex items-center gap-2 mb-3 mt-4">
          <span className="text-sm sm:text-base font-bold text-gray-900">
            Schedules
          </span>
          <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
            {schedules.length}
          </span>
        </div>

        {/* Loading state */}
        {loading ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 flex flex-col items-center">
            <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-xs text-gray-400">Loading schedules…</p>
          </div>

        ) : schedules.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
              <Calendar size={26} className="text-blue-400" />
            </div>
            <p className="text-sm font-semibold text-gray-700 mb-1">
              No schedules yet
            </p>
            <p className="text-xs text-gray-400 mb-5 max-w-xs">
              Create your first schedule to control when promo images display.
            </p>
            <button
              type="button"
              onClick={() => navigate("/create-schedule")}
              className="bg-blue-600 text-white text-sm font-bold px-5 py-2.5 rounded-full flex items-center gap-2"
            >
              <Plus size={15} /> Create Schedule
            </button>
          </div>

        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex items-center px-4 py-3 border-b border-gray-100 bg-gray-50">
              <span className="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Schedule Name
              </span>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide w-36 text-right">
                Actions
              </span>
            </div>

            {schedules.map((s, index) => (
              <div
                key={s._id}
                className={`flex items-center px-4 py-3.5 ${index !== schedules.length - 1 ? "border-b border-gray-100" : ""
                  }`}
              >
                <div className="flex-1 min-w-0 pr-3">
                  <p className="text-sm font-semibold text-gray-900 truncate mb-1">
                    {s.scheduleName}
                  </p>
                  <div className="flex gap-1.5 flex-wrap">
                    {/* Time */}
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-100 px-2 py-0.5 rounded-full">
                      <Clock size={9} />
                      {timeLabel(s)}
                    </span>
                    {/* Timezone */}
                    {timezoneLabel(s) && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-purple-50 text-purple-600 border border-purple-100 px-2 py-0.5 rounded-full">
                        {timezoneLabel(s)}
                      </span>
                    )}
                    {/* Days */}
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-gray-100 text-gray-500 border border-gray-200 px-2 py-0.5 rounded-full">
                      <Sun size={9} />
                      {daysLabel(s)}
                    </span>
                    {/* Image count */}
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-green-50 text-green-600 border border-green-100 px-2 py-0.5 rounded-full">
                      {s.digitalAdsId?.length || 0} images
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {/* Eye button */}
                  <button
                    type="button"
                    onClick={() => handleViewImages(s)}
                    className="p-2 rounded-full bg-gray-100 hover:bg-blue-50 text-gray-500 hover:text-[#2D5BE3] transition"
                    aria-label="View images"
                  >
                    <Eye size={13} />
                  </button>
                  {/* Edit button */}
                  <button
                    type="button"
                    onClick={() =>
                      navigate("/create-schedule", {
                        state: { editScheduleId: s._id },
                      })
                    }
                    className="border border-gray-200 p-1.5 rounded-lg"
                    aria-label="Edit schedule"
                  >
                    <Pencil size={13} className="text-gray-400" />
                  </button>
                  {/* Delete button */}
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(s._id)}
                    className="bg-red-50 p-1.5 rounded-lg"
                    aria-label="Delete schedule"
                  >
                    <Trash2 size={13} className="text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomTabBar />

      {/* ── Delete confirmation modal ── */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-5 sm:p-8">
          <div className="bg-white rounded-2xl p-5 sm:p-6 w-full max-w-xs shadow-2xl">
            <div className="bg-red-50 rounded-full w-12 h-12 flex items-center justify-center mb-4">
              <Trash2 size={22} className="text-red-500" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2">
              Delete schedule
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              Are you sure you want to delete{" "}
              <strong className="text-gray-900">
                &quot;
                {schedules.find((s) => s._id === deleteConfirm)?.scheduleName ||
                  "this schedule"}
                &quot;
              </strong>
              ? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 rounded-full text-sm disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 bg-red-500 text-white font-bold py-3 rounded-full text-sm flex items-center justify-center gap-2 disabled:opacity-40"
              >
                {deleting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Image preview modal (eye button) ── */}
      {showImagePreviewModal && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 sm:p-6"
          onClick={closePreview}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-5 sm:p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 leading-tight">
                  {viewingSchedule?.scheduleName || "Schedule Images"}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {(viewingSchedule?.digitalAdsId || []).length}{" "}
                  {(viewingSchedule?.digitalAdsId || []).length === 1
                    ? "image"
                    : "images"}{" "}
                  scheduled
                </p>
              </div>
              <button
                type="button"
                onClick={closePreview}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Loading */}
            {viewLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" />
                <p className="text-xs text-gray-400">Loading images…</p>
              </div>

            ) : !viewingSchedule?.digitalAdsId ||
              viewingSchedule.digitalAdsId.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <ImageIcon size={36} className="text-gray-300 mb-3" />
                <span className="text-sm font-medium">
                  No images in this schedule
                </span>
              </div>

            ) : (
              <div className="grid grid-cols-2 gap-3">
                {viewingSchedule.digitalAdsId.map((img) => (
                  <div key={img._id} className="flex flex-col gap-1.5">
                    <div className="rounded-2xl overflow-hidden aspect-video bg-gray-100 flex items-center justify-center">
                      {img.file_type === "video" ||
                        img.S3link?.match(/\.(mp4|webm|ogg|mov)$/i) ? (
                        <div className="relative w-full h-full">
                          <video
                            src={img.S3link}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <PlayCircle size={20} className="text-white" />
                          </div>
                        </div>
                      ) : (
                        <img
                          src={img.S3link}
                          alt={img.title}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 text-center truncate px-1">
                      {img.title || "Untitled"}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}